import React, { useState, useCallback } from 'react';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Collapsible,
  Box,
  Divider,
} from '@shopify/polaris';
import { PageEntrance } from '../components/ui';
import './FaqPage.css';

interface FaqItem {
  readonly id: string;
  readonly question: string;
  readonly answer: React.ReactNode;
  readonly category: string;
}

const FAQ_ITEMS: FaqItem[] = [
  // ── Product Limits ──────────────────────────────────────────
  {
    id: 'product-limit-starter',
    category: 'Product Limits',
    question: 'How does the product limit on my plan work?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          Every plan includes a tracked-product ceiling. When you run your first scan,
          Zelium fetches products from your Shopify catalog and starts tracking them up to
          your plan's limit.
        </Text>
        <Text as="p" variant="bodyMd">
          <strong>While you're under your plan's product cap:</strong> every new scan will
          discover and add any products you've added to your Shopify store since the last
          scan, up to the cap.
        </Text>
        <Text as="p" variant="bodyMd">
          <strong>Once you reach your plan's product limit:</strong> Zelium stops adding new
          products. Future scans will only rescore the products already in your tracked set
          whose score-relevant fields (title, description, images, etc.) have changed since
          the last scan.
        </Text>
        <Text as="p" variant="bodyMd">
          <strong>Next billing cycle:</strong> the product cap does not reset — it is a
          total tracked-products ceiling, not a monthly allowance. If you are already at
          your limit, new Shopify products will still not be picked up. To track additional
          products you need to upgrade to a higher plan, after which your next scan will
          immediately start discovering the products you were missing.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'product-limit-free',
    category: 'Product Limits',
    question: "I'm on the Free plan — can I change which products are tracked?",
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          No. On the Free plan, your tracked product set is <strong>pinned at your first
          scan</strong> and never changes. Zelium selects your most recently updated
          products at the time of that first scan, up to the Free plan's limit.
        </Text>
        <Text as="p" variant="bodyMd">
          Adding or deleting products in Shopify after that point has no effect on your
          tracked set. The same pinned products are rescored on every subsequent scan.
        </Text>
        <Text as="p" variant="bodyMd">
          The Free plan also limits the number of scans you can run per billing cycle.
          Upgrade to a paid plan to get unlimited scans and a dynamic scan set that picks
          up new products as you add them.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'product-limit-next-month',
    category: 'Product Limits',
    question: 'I added new products to my Shopify store — when will they be tracked?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          New products are picked up on your <strong>next scan</strong>, not automatically.
          When you trigger a scan (manually or via daily auto-scan on paid plans), Zelium
          streams your Shopify catalog and adds any products it hasn't seen before — up to
          your plan's product cap.
        </Text>
        <Text as="p" variant="bodyMd">
          The cap is a <strong>total tracked products</strong> ceiling, not a "products per
          month" allowance. Once you reach it, new Shopify products are only picked up if
          you upgrade your plan.
        </Text>
        <Text as="p" variant="bodyMd">
          <strong>Example:</strong> If you have a few slots remaining before your cap and
          add more new products in Shopify than you have room for, the next scan will fill
          the remaining slots. The rest won't be tracked until you upgrade.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'product-limit-rescore',
    category: 'Product Limits',
    question: 'When are my existing products rescored?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          Zelium does not blindly rescore every product on each scan. A product is only
          rescored when its <strong>score-relevant fields</strong> have changed — for
          example its title, description, images, tags, or structured data.
        </Text>
        <Text as="p" variant="bodyMd">
          Shopify notifies Zelium of product changes via webhooks. When a change touches a
          field that affects your discoverability score, the product is flagged for rescoring
          and will be picked up on the next scan.
        </Text>
        <Text as="p" variant="bodyMd">
          If nothing score-relevant has changed, the product keeps its current score and is
          skipped — saving time and AI credits.
        </Text>
      </BlockStack>
    ),
  },

  // ── AI Credits ──────────────────────────────────────────────
  {
    id: 'llm-credits-how-counted',
    category: 'AI Credits',
    question: 'How are AI credits counted, and when do they reset?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          Each AI-powered action — such as generating an auto-fix suggestion or creating an
          AI product summary — consumes <strong>1 credit</strong>.
        </Text>
        <Text as="p" variant="bodyMd">
          Your monthly credit allowance resets at the start of each billing cycle. Each paid
          plan includes a set number of credits per month — higher plans include more. The
          Free plan has no AI credits; those features are locked until you upgrade.
        </Text>
        <Text as="p" variant="bodyMd">
          Unused credits do <strong>not</strong> roll over to the next month.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'llm-credits-exhausted',
    category: 'AI Credits',
    question: 'What happens when I run out of AI credits?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          Zelium blocks further AI-powered actions (auto-fix generation, AI summaries) for
          the rest of the current billing cycle. Scoring, fix suggestions display, and
          compliance checks remain available — only the AI generation actions are paused.
        </Text>
        <Text as="p" variant="bodyMd">
          Credits reset automatically at the start of your next billing cycle, or you can
          upgrade to a higher plan to get a larger monthly allowance immediately.
        </Text>
      </BlockStack>
    ),
  },

  // ── Score History ────────────────────────────────────────────
  {
    id: 'score-history-retention',
    category: 'Score History',
    question: 'How long is my score history kept?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          Your store score trend chart retains data for a rolling window that varies by
          plan — Free has the shortest window, Scale has the longest. Higher plans give you
          more historical context to track improvement over time.
        </Text>
        <Text as="p" variant="bodyMd">
          Data older than your retention window is automatically purged. Upgrading your plan
          preserves the history already stored — you won't lose data you already have.
        </Text>
      </BlockStack>
    ),
  },

  // ── Suggestions ─────────────────────────────────────────────
  {
    id: 'suggestions-free-limit',
    category: 'Fix Suggestions',
    question: 'Why do I only see a few fix suggestions per product on Free?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          The Free plan caps <strong>visible</strong> suggestions per product to a small
          number — the most impactful ones ranked by estimated score improvement.
        </Text>
        <Text as="p" variant="bodyMd">
          Zelium generates more suggestions internally, but the Free tier shows only the
          top ones with a prompt indicating how many more are available.
        </Text>
        <Text as="p" variant="bodyMd">
          Upgrading to a paid plan removes the cap and shows all suggestions with
          per-suggestion score-impact estimates.
        </Text>
      </BlockStack>
    ),
  },

  // ── Auto-Fix Undo ────────────────────────────────────────────
  {
    id: 'autofix-undo-window',
    category: 'Auto-Fix',
    question: 'How long can I undo an auto-fix?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          After you apply an auto-fix, Zelium stores the original value so you can revert
          with one click. The undo window depends on your plan — higher plans give you a
          longer window to revert.
        </Text>
        <Text as="p" variant="bodyMd">
          Auto-fix is not available on the Free plan.
        </Text>
        <Text as="p" variant="bodyMd">
          After the window expires, one-click revert is no longer available. You can still
          edit the product manually in Shopify Admin to restore the original value.
        </Text>
      </BlockStack>
    ),
  },

  // ── Crawl Simulations ────────────────────────────────────────
  {
    id: 'crawl-sim-credits',
    category: 'AI Crawl Simulation',
    question: 'How are crawl simulation credits counted?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          AI Crawl Simulation is available on the <strong>Scale plan only</strong>.
        </Text>
        <Text as="p" variant="bodyMd">
          <strong>Deep simulations</strong> (LLM-powered — Zelium reads your product page
          the way an AI crawler would and reports what it can and can't extract) have their
          own monthly allowance, tracked separately from your AI credits.
        </Text>
        <Text as="p" variant="bodyMd">
          <strong>Quick simulations</strong> (rule-based structural checks — schema tags,
          Open Graph, canonical URL, etc.) do not consume any credits and run instantly.
        </Text>
      </BlockStack>
    ),
  },

  // ── Billing & Subscription ──────────────────────────────────
  {
    id: 'billing-cancel',
    category: 'Billing & Subscription',
    question: 'What happens when I cancel my subscription?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          You keep full access to your current plan until the end of the billing period
          you've already paid for. After that, your account is automatically downgraded to
          the Free plan.
        </Text>
        <Text as="p" variant="bodyMd">
          Your data (scores, history, suggestions) is retained, but premium features become
          read-only or deactivated. If you had a JSON-LD feed or llms.txt enabled, those
          files are removed from the CDN and storefront injection stops.
        </Text>
        <Text as="p" variant="bodyMd">
          You can re-subscribe at any time to restore paid features.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'billing-upgrade-downgrade',
    category: 'Billing & Subscription',
    question: 'Does upgrading or downgrading take effect immediately?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          <strong>Upgrades</strong> take effect immediately — you get the new product limit
          and AI credit allowance right away, and your billing cycle restarts.
        </Text>
        <Text as="p" variant="bodyMd">
          <strong>Downgrades</strong> take effect at your next billing cycle renewal — you
          keep your current tier's benefits until then.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'billing-payment-failure',
    category: 'Billing & Subscription',
    question: 'What happens if my payment fails?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          If Shopify freezes your subscription due to a failed payment, your account is
          immediately restricted to Free plan capabilities. Feed access is revoked.
        </Text>
        <Text as="p" variant="bodyMd">
          Once payment is resolved through Shopify, your subscription reactivates
          automatically and your paid features are restored.
        </Text>
      </BlockStack>
    ),
  },

  // ── Scanning & Sync ─────────────────────────────────────────
  {
    id: 'scanning-daily-auto',
    category: 'Scanning & Sync',
    question: 'Do I get daily automatic scans?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          Yes — all paid plans include a daily auto-scan that runs in the early morning
          (UTC). It discovers new products (if you're under your cap) and rescores any
          tracked products whose data has changed since the last scan.
        </Text>
        <Text as="p" variant="bodyMd">
          The Free plan does not include daily auto-scans. You can disable daily scans in
          your settings if you prefer to scan manually.
        </Text>
        <Text as="p" variant="bodyMd">
          If a manual scan is already running when the daily scan fires, it is silently
          skipped — no duplicate work.
        </Text>
      </BlockStack>
    ),
  },
  {
    id: 'scanning-webhook-sync',
    category: 'Scanning & Sync',
    question: 'What does Webhook Real-Time Sync do?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          Webhook Sync (available on higher-tier plans) listens for Shopify product changes
          in near real-time. When you update a product in Shopify Admin, Zelium is notified
          within minutes and flags the product for rescoring on the next scan.
        </Text>
        <Text as="p" variant="bodyMd">
          Without Webhook Sync, product changes are still detected — but only during your
          next scheduled daily scan or manual scan. Webhook Sync makes the feedback loop
          faster.
        </Text>
        <Text as="p" variant="bodyMd">
          A daily reconciliation job catches any webhooks that may have been missed, so no
          changes slip through the cracks.
        </Text>
      </BlockStack>
    ),
  },

  // ── Data & Privacy ──────────────────────────────────────────
  {
    id: 'privacy-uninstall',
    category: 'Data & Privacy',
    question: 'What happens to my data if I uninstall the app?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          When you uninstall, your data is soft-deleted and kept for a short grace period.
          If you reinstall during that window, all your scores, history, and settings are
          restored automatically.
        </Text>
        <Text as="p" variant="bodyMd">
          After the grace period, all data is permanently and irreversibly purged —
          products, scores, suggestions, scan history, and store settings.
        </Text>
        <Text as="p" variant="bodyMd">
          Zelium never stores customer personal information. The only data we hold is
          product catalog data (titles, descriptions, images) and your store's
          discoverability scores.
        </Text>
      </BlockStack>
    ),
  },

  // ── Feed & Storefront ───────────────────────────────────────
  {
    id: 'feed-how-it-works',
    category: 'Feed & Storefront',
    question: 'How does the JSON-LD feed work? Does it modify my theme?',
    answer: (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          Zelium uses Shopify metafields and a Theme App Extension (App Embed Block) to
          inject structured data into your storefront. It does <strong>not</strong> edit
          your theme files directly.
        </Text>
        <Text as="p" variant="bodyMd">
          Each tracked product gets a JSON-LD <code>&lt;script&gt;</code> tag via its
          metafield. A separate product feed, llms.txt, and AI sitemap are hosted on
          Zelium's CDN. The feed is auto-regenerated daily.
        </Text>
        <Text as="p" variant="bodyMd">
          If you deactivate the feed or downgrade to a plan without feed access, the
          metafield is cleared and all CDN files are deleted immediately.
        </Text>
      </BlockStack>
    ),
  },
];

const CATEGORIES = [...new Set(FAQ_ITEMS.map((f) => f.category))];

function FaqAccordionItem({ item }: Readonly<{ item: FaqItem }>): React.ReactElement {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  return (
    <Box>
      <button
        className="faq-question-btn"
        onClick={toggle}
        {...{ 'aria-expanded': open, 'aria-controls': `faq-answer-${item.id}` }}
        type="button"
      >
        <InlineStack align="space-between" blockAlign="center" wrap={false}>
          <Text as="span" variant="bodyMd" fontWeight="semibold">
            {item.question}
          </Text>
          <span className={`faq-chevron${open ? ' faq-chevron--open' : ''}`} aria-hidden="true">
            ▾
          </span>
        </InlineStack>
      </button>
      <Collapsible
        id={`faq-answer-${item.id}`}
        open={open}
        transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
      >
        <Box paddingBlockStart="300" paddingBlockEnd="200" paddingInlineStart="200">
          {item.answer}
        </Box>
      </Collapsible>
    </Box>
  );
}

export function FaqPage(): React.JSX.Element {
  return (
    <PageEntrance>
      <Page
        title="FAQ"
        backAction={{ content: 'Dashboard', url: '/dashboard' }}
      >
        <BlockStack gap="400">
          <div className="z-hero">
            <div className="z-hero-content">
              <h2 className="z-hero-title">❓ Frequently Asked Questions</h2>
              <p className="z-hero-subtitle">
                How plan limits work, how credits are counted, and what changes when you upgrade.
              </p>
            </div>
          </div>

          <BlockStack gap="500">
            {CATEGORIES.map((category) => (
              <Card key={category}>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingSm">
                    {category}
                  </Text>
                  <Divider />
                  <BlockStack gap="0">
                    {FAQ_ITEMS.filter((f) => f.category === category).map((item, idx, arr) => (
                      <React.Fragment key={item.id}>
                        <FaqAccordionItem item={item} />
                        {idx < arr.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </BlockStack>
                </BlockStack>
              </Card>
            ))}
          </BlockStack>
        </BlockStack>
      </Page>
    </PageEntrance>
  );
}
